/**
 * Commands: vordefinierte Befehlsgruppen (cmd_defs) — Toggles (on/off +
 * Autoload), Actions (mit Bestätigung), Params ({X}-Template). Ausführung über
 * rcon_batch (roh), Toggle-/Autoload-Zustand über cmd_state(_save).
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { useRef } from "preact/hooks";
import { api, activeServer } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

type CmdItem =
  | { id: string; label: string; type: "toggle"; desc?: string; on: string[]; off: string[] }
  | { id: string; label: string; type: "action"; desc?: string; confirm?: boolean; cmds: string[] }
  | { id: string; label: string; type: "param"; desc?: string; placeholder?: string; tpl: string };
interface CmdGroup { group: string; items: CmdItem[] }
interface StateRec { state?: string | null; autoload?: boolean; desc?: string | null }

export type CmdLoadState = "loading" | "ok" | "error";
export type CmdBody = "loading" | "error" | "empty" | "grid";

export interface CommandsData { ok: boolean; groups: CmdGroup[]; state: Record<string, StateRec> }

/**
 * Holt Command-Defs + State robust: wirft NIE (api.call wirft bei 401/Netz —
 * hier gefangen) und liefert ein normalisiertes Ergebnis. `ok:false` heißt
 * "Defs nicht ladbar" → die View zeigt einen Error-State statt still leer zu
 * bleiben. State-Fehler sind tolerierbar (Defs sind das Wesentliche).
 */
export async function fetchCommands(
  callDefs: () => Promise<ApiResponse & { groups?: CmdGroup[] }>,
  callState: () => Promise<ApiResponse & { state?: Record<string, StateRec> }>,
): Promise<CommandsData> {
  // Unabhängig (allSettled): ein State-Fehler (reject ODER ok:false) darf die
  // Defs NICHT killen — Defs sind das Wesentliche, State ist tolerierbar.
  const [dR, sR] = await Promise.allSettled([callDefs(), callState()]);
  const d = dR.status === "fulfilled" ? dR.value : null;
  if (!d || !d.ok || !Array.isArray(d.groups)) {
    return { ok: false, groups: [], state: {} };
  }
  const state = sR.status === "fulfilled" && sR.value.ok && sR.value.state ? sR.value.state : {};
  return { ok: true, groups: d.groups, state };
}

/**
 * Entscheidet, was der View-Body zeigt — nie still leer:
 * - Fehler → Error-State (mit Retry),
 * - bereits Gruppen vorhanden → Grid (auch während eines Reloads, kein Flackern),
 * - sonst noch am Laden → Loading,
 * - sonst (ok, aber 0 Gruppen) → Empty-State.
 */
export function commandsBodyState(loadState: CmdLoadState, groupCount: number): CmdBody {
  if (loadState === "error") return "error";
  if (groupCount > 0) return "grid";
  if (loadState === "loading") return "loading";
  return "empty";
}

export function Commands() {
  const groups = useSignal<CmdGroup[]>([]);
  const state = useSignal<Record<string, StateRec>>({});
  const params = useSignal<Record<string, string>>({});
  const busy = useSignal("");
  const edit = useSignal(false);
  const loadState = useSignal<CmdLoadState>("loading");
  // Race-Guard: nur die jeweils NEUESTE Lade-Antwort darf den State setzen
  // (eine veraltete Antwort nach Serverwechsel/Reload überschreibt nichts mehr).
  const loadGen = useRef(0);

  async function load() {
    const gen = ++loadGen.current;
    // WICHTIG: hier KEIN `groups.value` lesen — der useSignalEffect unten würde
    // groups sonst als Dependency tracken und bei jedem groups-Set neu feuern
    // (Endlos-Reload). `commandsBodyState` hält das Grid sichtbar, solange
    // Gruppen da sind, also flackert ein "loading" bei bestehenden Daten nicht.
    loadState.value = "loading";
    const res = await fetchCommands(
      () => api.call<ApiResponse & { groups?: CmdGroup[] }>("cmd_defs"),
      () => api.call<ApiResponse & { state?: Record<string, StateRec> }>("cmd_state"),
    );
    if (gen !== loadGen.current) return; // veraltet → verwerfen
    if (res.ok) {
      groups.value = res.groups;
      state.value = res.state;
      loadState.value = "ok";
    } else {
      loadState.value = "error";
    }
  }
  // Beim Mount UND bei jedem Serverwechsel neu laden (Daten sind zwar global,
  // aber so wird nach einem fehlgeschlagenen Erstladen beim Wechsel neu versucht).
  // Kein Endlos-Poll: feuert nur, wenn sich `activeServer` ändert.
  useSignalEffect(() => { void activeServer.value; void load(); });

  async function runBatch(label: string, cmds: string[]): Promise<boolean> {
    const sv = activeServer.value;
    if (!sv) return false;
    busy.value = label;
    try {
      const r = await api.call<ApiResponse & { count: number }>("rcon_batch", { server: sv, label, cmds });
      if (r.ok) toast(`${label}: ${r.count} ${t("commands.commandsWord")} ${t("commands.sentSuffix")}`, "ok");
      else toast(String(r.error ?? t("common.error")), "err");
      return r.ok;
    } finally {
      busy.value = "";
    }
  }

  async function saveState(id: string, patch: StateRec) {
    const r = await api.call<ApiResponse & { state: Record<string, StateRec> }>("cmd_state_save", { id, ...patch });
    if (r.ok) state.value = r.state;
  }

  async function setToggle(it: Extract<CmdItem, { type: "toggle" }>, on: boolean) {
    const okExec = await runBatch(it.label, on ? it.on : it.off);
    if (okExec) await saveState(it.id, { state: on ? "on" : "off" });
  }

  function Item({ it }: { it: CmdItem }) {
    const st = state.value[it.id];
    if (it.type === "toggle") {
      const on = st?.state === "on";
      return (
        <div class="row" style="justify-content:space-between;gap:var(--s3);padding:var(--s2) 0">
          <div style="min-width:0">
            <div style="font-weight:550">{it.label}</div>
            {it.desc && <div class="caption">{it.desc}</div>}
          </div>
          <div class="row">
            <label class="caption row" style="gap:6px" title={t("commands.autoTitle")}>
              <input type="checkbox" checked={!!st?.autoload} onChange={(e) => void saveState(it.id, { autoload: (e.target as HTMLInputElement).checked })} />
              auto
            </label>
            <button class={`btn sm ${on ? "primary" : ""}`} disabled={busy.value === it.label} onClick={() => void setToggle(it, true)}>On</button>
            <button class={`btn sm ${!on && st?.state === "off" ? "primary" : ""}`} disabled={busy.value === it.label} onClick={() => void setToggle(it, false)}>Off</button>
          </div>
        </div>
      );
    }
    if (it.type === "action") {
      return (
        <div class="row" style="justify-content:space-between;gap:var(--s3);padding:var(--s2) 0">
          <div style="min-width:0"><div style="font-weight:550">{it.label}</div>{it.desc && <div class="caption">{it.desc}</div>}</div>
          <button
            class="btn sm"
            disabled={busy.value === it.label}
            onClick={() => { if (!it.confirm || confirm(`${t("commands.confirmRunPre")}${it.label}${t("commands.confirmRunPost")}`)) void runBatch(it.label, it.cmds); }}
          >
            {t("common.execute")}
          </button>
        </div>
      );
    }
    // param
    const v = params.value[it.id] ?? "";
    return (
      <div class="stack" style="gap:6px;padding:var(--s2) 0">
        <div style="font-weight:550">{it.label}</div>
        {it.desc && <div class="caption">{it.desc}</div>}
        <div class="row">
          <input class="input mono grow" placeholder={it.placeholder ?? ""} value={v} onInput={(e) => (params.value = { ...params.value, [it.id]: (e.target as HTMLInputElement).value })} />
          <button class="btn sm" disabled={busy.value === it.label || v === ""} onClick={() => void runBatch(it.label, [it.tpl.replace(/\{X\}/g, v)])}>{t("common.apply")}</button>
        </div>
      </div>
    );
  }

  if (!activeServer.value) return (<><PageHead title={t("nav.commands")} /><NoServer /></>);

  const items: PanelItem[] = groups.value.map((g) => ({
    id: `grp:${g.group}`,
    title: g.group,
    defaultSpan: 6,
    render: () => (
      <Panel title={g.group}>
        <div class="stack" style="gap:var(--s2)">
          {g.items.map((it) => <Item key={it.id} it={it} />)}
        </div>
      </Panel>
    ),
  }));

  const body = commandsBodyState(loadState.value, groups.value.length);

  return (
    <div class="view-enter">
      <PageHead title={t("nav.commands")} sub={t("commands.sub")}>
        {body === "grid" && (
          <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        )}
      </PageHead>
      {body === "grid" && <PanelGrid view="commands" items={items} edit={edit} />}
      {body === "loading" && (
        <Panel title={t("nav.commands")}><div class="empty">{t("commands.loading")}</div></Panel>
      )}
      {body === "error" && (
        <Panel title={t("nav.commands")}>
          <div class="empty stack" style="gap:var(--s3)">
            <div class="banner error">{t("commands.loadError")}</div>
            <button class="btn sm" onClick={() => void load()}>↻ {t("commands.retry")}</button>
          </div>
        </Panel>
      )}
      {body === "empty" && (
        <Panel title={t("nav.commands")}><div class="empty">{t("commands.empty")}</div></Panel>
      )}
    </div>
  );
}
